import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { getSession, setSession, logout as performLogout, type AuthUser } from '@/lib/auth';
import { api } from '@/lib/api';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getSession());

  const login = useCallback(async (email: string, password: string) => {
    try {
      const result = await api.auth.login(email, password);
      if (result.ok && result.user) {
        setSession(result.user);
        setUser(result.user);
        return { ok: true };
      }
      return { ok: false, error: 'Credenciales inválidas' };
    } catch (err: any) {
      return { ok: false, error: err.message || 'Error al iniciar sesión' };
    }
  }, []);

  const logout = useCallback(async () => {
    await api.auth.logout();
    performLogout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return ctx;
}
